import React from "react";

import { getEmployeeForm, getAdminMode, getBenefitsAdmin, getCompanyIDToken, getProcessOverviewData } from "EmployeeApplication/api";
import { getEmployeeHashId } from "EmployeeApplication/selectors";
import {
  basicInformationURL,
  familyInformationURL,
  chooseMedicalPlanURL,
  chooseDentalPlanURL,
  chooseVisionPlanURL,
  chooseTermLifePlanURL,
  signURL,
} from "EmployeeApplication/urls";
import isIntuit from "Intuit/isIntuit";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { formatNameFML } from "Utils/formatName";
import { routerUrlHelper } from "Utils/routerUrl";

import BodyText from "Cobalt/BodyText";
import { LinkButton } from "Cobalt/Button";
import { ProcessOverview as BaseProcessOverview, ProcessStep } from "Common/ProcessOverview";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import type { EmployeeForm } from "Utils/types";

type StepInfo = {
  status: "past" | "present" | "future";
  url: string;
};

const TopHeaderSection = (employee: EmployeeForm, companyIDToken: string, benefitsAdmin: boolean) => {
  const employerRenewalURL = benefitsAdmin ? "/benefits-administration/renewal/status" : "/employer-renewal/employee-status";
  const returnURL = `${employerRenewalURL}${companyIDToken ? `?company_id_token=${companyIDToken}` : ""}`;

  return (
    <BodyText mt={3} ml={5}>
      You are viewing {formatNameFML(employee)}.&nbsp;
      <LinkButton
        pl={1}
        py={0}
        color="link"
        onClick={() => {
          window.location = appendAuthParams(routerUrlHelper(returnURL));
        }}
      >
        Return to Company Renewal.
      </LinkButton>
    </BodyText>
  );
};

const stepStatus = (location: string, urls: Array<string>, innerStepsStatus: Array<boolean>): StepInfo => {
  let status;

  if (urls.includes(location)) {
    status = "present";
  } else if (innerStepsStatus.every((stepStatusBool) => stepStatusBool)) {
    status = "past";
  } else {
    status = "future";
  }
  const firstFalseIdx = innerStepsStatus.indexOf(false);

  const url = firstFalseIdx === -1 ? urls[0] : urls[firstFalseIdx];

  return {
    status,
    url,
  };
};

const useStepDataByPage = (adminMode) => {
  const location = useLocation().pathname;
  const data = useSelector(getProcessOverviewData);
  const employeeHashId = useSelector(getEmployeeHashId);

  return {
    basicInformation: stepStatus(
      location,
      [basicInformationURL(adminMode, true, employeeHashId, false), familyInformationURL(adminMode, true, employeeHashId, false)],

      [data.basicInformation, data.familyInformation],
    ),

    chooseCoverage: stepStatus(
      location,
      [
        chooseMedicalPlanURL(adminMode, true, employeeHashId, false),
        chooseDentalPlanURL(adminMode, true, employeeHashId, false),
        chooseVisionPlanURL(adminMode, true, employeeHashId, false),
        chooseTermLifePlanURL(adminMode, true, employeeHashId, false),
      ],

      [data.chooseMedicalPlan, data.chooseDentalPlan, data.chooseVisionPlan, data.chooseTermLifePlan],
    ),

    sign: stepStatus(location, [signURL(adminMode, true, employeeHashId, false)], [data.sign]),
  };
};

const ProcessOverview = () => {
  const employee = useSelector(getEmployeeForm);
  const adminMode = useSelector(getAdminMode) || false;
  const benefitsAdmin = useSelector(getBenefitsAdmin);
  const stepData = useStepDataByPage(adminMode);
  const companyIDToken = useSelector(getCompanyIDToken);

  return (
    <BaseProcessOverview topHeaderSection={adminMode && TopHeaderSection(employee, companyIDToken, benefitsAdmin)}>
      <ProcessStep
        title="Review Basic Information"
        component={Link}
        to={stepData.basicInformation.url}
        status={stepData.basicInformation.status}
        order={isIntuit() ? "first" : undefined}
      />

      <ProcessStep
        title="Choose Plan"
        component={Link}
        to={stepData.chooseCoverage.url}
        status={stepData.chooseCoverage.status}
        order={isIntuit() ? "middle" : undefined}
      />

      <ProcessStep title="Signature" component={Link} to={stepData.sign.url} status={stepData.sign.status} order={isIntuit() ? "last" : undefined} />
    </BaseProcessOverview>
  );
};

export default ProcessOverview;
